import { useState, useEffect, useRef } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { IconDisplay } from '@/components/ui/icon-picker';
import { useLanguage } from '@/contexts/LanguageContext';

type BilingualField = string | { ar: string; en: string };

interface Program {
  id: string;
  title: BilingualField;
  description: BilingualField;
  icon: string;
  image: string;
  order: number;
}

export function LandingPrograms() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState<number | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const { t, language } = useLanguage();

  const localize = (field: BilingualField): string => {
    if (typeof field === 'string') return field;
    return field[language] || field.ar || field.en || '';
  };

  // Create infinite loop by triplicating the array (reversed order)
  const infinitePrograms = programs.length > 0 
    ? [...programs.slice().reverse(), ...programs.slice().reverse(), ...programs.slice().reverse()] 
    : [];

  useEffect(() => {
    fetchPrograms();
  }, []);

  // Initialize scroll to middle set after programs load
  useEffect(() => {
    if (programs.length > 0 && containerRef.current) {
      const container = containerRef.current;
      // Wait for layout to complete
      setTimeout(() => {
        const cardElement = container.children[0] as HTMLElement;
        if (cardElement) {
          // Scroll to the start of the middle set (second copy)
          const middlePosition = cardElement.offsetWidth * programs.length;
          container.scrollLeft = middlePosition;
        }
      }, 100);
    }
  }, [programs.length]);

  // Monitor scroll position and reset if needed for infinite effect
  useEffect(() => {
    const container = containerRef.current;
    if (!container || programs.length === 0) return;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      const cardElement = container.children[0] as HTMLElement;
      if (!cardElement) return;

      const cardWidth = cardElement.offsetWidth;
      const scrollLeft = container.scrollLeft;
      const totalOriginalWidth = cardWidth * programs.length;

      // If scrolled past the end of second set, jump to start of second set
      if (scrollLeft >= totalOriginalWidth * 2) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
      // If scrolled before the start of second set, jump to end of second set
      else if (scrollLeft < totalOriginalWidth) {
        isScrollingRef.current = true;
        container.scrollLeft = totalOriginalWidth * 2 - cardWidth;
        setTimeout(() => { isScrollingRef.current = false; }, 50);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [programs.length]);

  // Auto-scroll effect
  useEffect(() => {
    if (programs.length === 0) return;

    const interval = setInterval(() => {
      if (!isHovering && containerRef.current && !isScrollingRef.current) {
        const container = containerRef.current;
        const cardElement = container.children[0] as HTMLElement;
        if (!cardElement) return;
        
        const scrollAmount = cardElement.offsetWidth;
        
        // Scroll left (decrease scrollLeft) - the infinite scroll handler will take care of wrapping
        container.scrollTo({ 
          left: container.scrollLeft - scrollAmount, 
          behavior: 'smooth' 
        });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [isHovering, programs.length]);

  const fetchPrograms = async () => {
    try {
      const programsQuery = query(
        collection(db, 'landing_programs'),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(programsQuery);

      const programsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Program[];

      setPrograms(programsData);
    } catch (err) {
      console.error('Error fetching programs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (direction: 'prev' | 'next') => {
    const container = containerRef.current;
    if (!container) return;

    // Get the width of a single card (first child element)
    const cardElement = container.children[0] as HTMLElement;
    if (!cardElement) return;
    
    const scrollAmount = cardElement.offsetWidth;
    
    if (direction === 'next') {
      container.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    } else {
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">{t('landing.common.loading')}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="programs" className="py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-[#004250] mb-12">{t('landing.programs.title')}</h2>
        
        <div 
          className="relative group"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div 
            ref={containerRef}
            dir="ltr"
            className="flex overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            {infinitePrograms.map((program, index) => {
              const title = localize(program.title);
              const description = localize(program.description);
              
              return (
              <div
                key={`${program.id}-${index}`}
                className="min-w-full md:min-w-[33.333%] px-4 py-8"
                onClick={() => setSelectedProgram(index % programs.length)}
              >
                <div className="bg-gray-50 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={program.image}
                      alt={title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <IconDisplay iconName={program.icon} className="w-6 h-6" />
                        {title}
                      </h3>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 line-clamp-2">{description}</p>
                    <button className="mt-4 text-[#7dda55] hover:text-[#004250] font-semibold flex items-center gap-2">
                      {t('landing.common.readMore')} ←
                    </button>
                  </div>
                </div>
              </div>
            )}
            )}
          </div>

          <button
            onClick={() => handleNavigation('next')}
            className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <button
            onClick={() => handleNavigation('prev')}
            className="absolute left-0 top-1/2 -translate-y-1/2 translate-x-2 bg-white/90 hover:bg-white text-[#004250] p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        </div>
      </div>

      {selectedProgram !== null && (() => {
        const program = programs[selectedProgram];
        const title = localize(program.title);
        const description = localize(program.description);
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="relative h-64">
              <img
                src={program.image}
                alt={title}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => setSelectedProgram(null)}
                className="absolute left-4 top-4 bg-white/10 backdrop-blur-sm text-white rounded-full p-2 hover:bg-white hover:text-[#004250] transition-colors"
              >
                ✕
              </button>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-8">
                <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                  <IconDisplay iconName={program.icon} className="w-8 h-8" />
                  {title}
                </h3>
              </div>
            </div>
            <div className="p-8">
              <p className="text-gray-600 mb-6 leading-relaxed">
                {description}
              </p>
              <button className="bg-[#7dda55] text-white px-6 py-2 rounded-full font-semibold hover:bg-[#004250] transition-colors">
                {t('landing.programs.registerNow')}
              </button>
            </div>
          </div>
        </div>
      )})()}
    </section>
  );
}

